// Модерация отзывов по жалобам продавцов (fd-admin-05): /admin/review-complaints*.
//
// Свой маленький клиент раздела, как у рекламы (ad-campaigns-api.ts):
// раздел живёт только на шлюзе (мок-режима нет), а общий интерфейс Api и его
// мок правят все пакеты сразу. Типы — из корневого barrel @osmix/contracts.
import type { AdminReviewComplaint, AdminReviewComplaintPage, ReviewComplaintStatus } from "@osmix/contracts";
import { ApiClientError, createApiClient } from "@osmix/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/errors";
import { getAuthToken } from "@/api/token";

const readEnv = (read: () => string | undefined): string | undefined => {
	try {
		return read();
	} catch {
		return undefined;
	}
};

// Литерал целиком: Bun подставляет BUN_PUBLIC_* при сборке только так.
const GATEWAY_URL = readEnv(() => process.env.BUN_PUBLIC_API_GATEWAY_URL) ?? "";

export const reviewModerationAvailable = Boolean(GATEWAY_URL);

const client = GATEWAY_URL ? createApiClient({ baseUrl: GATEWAY_URL, getBearerToken: getAuthToken }) : null;

type Envelope<T> = { success: true; data: T } | { success: false; error?: { code?: string; message?: string } };

const call = async <T>(
	path: string,
	opts: { method?: string; query?: Record<string, string | number | undefined>; body?: unknown } = {},
): Promise<T> => {
	if (!client) throw new ApiError("Модерация отзывов доступна только при подключении к шлюзу", 503);
	try {
		const payload = await client.request<Envelope<T>>(path, opts);
		if (payload?.success) return payload.data;
		throw new ApiError(payload?.error?.message ?? "Request failed", 400, payload?.error?.code);
	} catch (error) {
		if (error instanceof ApiError) throw error;
		if (error instanceof ApiClientError) {
			const body = error.body as Envelope<T> | null;
			const err = body && body.success === false ? body.error : undefined;
			throw new ApiError(err?.message ?? `Request failed: ${error.status}`, error.status, err?.code);
		}
		throw error;
	}
};

export type ReviewComplaintFilter = { status: ReviewComplaintStatus; q?: string; page?: number; pageSize?: number };

export const adminReviewComplaints = {
	list: (filter: ReviewComplaintFilter) =>
		call<AdminReviewComplaintPage>("/admin/review-complaints", {
			query: { status: filter.status, q: filter.q || undefined, page: filter.page, pageSize: filter.pageSize },
		}),
	count: () => call<{ open: number }>("/admin/review-complaints/count"),
	get: (id: string) => call<AdminReviewComplaint>(`/admin/review-complaints/${encodeURIComponent(id)}`),
	decide: (id: string, action: "hide" | "keep", reason: string) =>
		call<AdminReviewComplaint>(`/admin/review-complaints/${encodeURIComponent(id)}/decision`, {
			method: "POST",
			body: { action, reason },
		}),
	restore: (reviewId: string, reason: string) =>
		call<{ reviewId: string; hidden: false }>(`/admin/reviews/${encodeURIComponent(reviewId)}/restore`, {
			method: "POST",
			body: { reason },
		}),
};

const KEY = ["admin-review-complaints"] as const;

export const useReviewComplaints = (filter: ReviewComplaintFilter) =>
	useQuery({
		queryKey: [...KEY, "list", filter.status, filter.q ?? "", filter.page ?? 1, filter.pageSize ?? 20],
		queryFn: () => adminReviewComplaints.list(filter),
		enabled: reviewModerationAvailable,
		placeholderData: (prev) => prev,
	});

export const useReviewComplaint = (id: string | null) =>
	useQuery({
		queryKey: [...KEY, "one", id ?? ""],
		queryFn: () => adminReviewComplaints.get(id ?? ""),
		enabled: reviewModerationAvailable && Boolean(id),
	});

export const useReviewModeration = () => {
	const queryClient = useQueryClient();
	const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
	return {
		decide: useMutation({
			mutationFn: ({ id, action, reason }: { id: string; action: "hide" | "keep"; reason: string }) =>
				adminReviewComplaints.decide(id, action, reason),
			onSuccess: refresh,
		}),
		restore: useMutation({
			mutationFn: ({ reviewId, reason }: { reviewId: string; reason: string }) =>
				adminReviewComplaints.restore(reviewId, reason),
			onSuccess: refresh,
		}),
	};
};
